import i18n from "i18next";
import { ProgramContentType } from "data/BlockComponent";
import {
  CellType,
  FeedbackType,
  MultipleSelectionType,
  PercentageType,
  SelfCheckType,
  SingleSelectionType,
  TextareaType,
  TypographyFromDataType,
  TypographyType,
} from "data/CellComponent";
import { isTaskIdValid } from "data/programData";
import {
  getPatientIdFromTaskKey,
  getPercentageSum,
  getReferenceCell,
  getTaskIdFromTaskKey,
  getTextMappingFromDataMapping,
} from "logic/logics";
import {
  getCellKey,
  getNextBlockIndex,
  getOptionCellKey,
  isCellEmpty,
} from "logic/autoFill";
import {
  UserSimBlock,
  UserSimField,
  UserSimFill,
  UserSimItem,
  UserSimRule,
  UserSimStreamRequest,
  UserSimVisibleIf,
} from "api/userSimApi";

type TextMappingParams = Parameters<typeof getTextMappingFromDataMapping>[0];

export type UserSimRequestParams = {
  taskKey: string;
  title: string;
  content: ProgramContentType[];
  t: TextMappingParams["t"]; // keyPrefix: task.activity.<taskId>.<translationVersion> (Cell.tsx 와 같음)
  t_common: (key: string) => string; // keyPrefix: task.activity.common
  accessToken: string;
  pid?: number;
  taskDataState: TextMappingParams["taskDataState"];
  setTaskDataState: TextMappingParams["setTaskDataState"];
  thoughtRecordKey?: string;
};

const INPUT_TYPES = [
  "textarea",
  "feedback",
  "percentage",
  "buttongroup",
  "select",
  "buttongroup_multipleChoice",
];

type OptionItem = { label: string } | { field: UserSimField };

// 생각기록지 생각함정별 생각 칸 — 하나 이상만 채우면 된다 (logics.isBlockUserFieldFilled)
const THOUGHT_TRAP_ID_PREFIX = "thoughtWithThoughtTrap";

// 코치·AI 코멘트 블록 (CommentWithTextareaInput·AiCommentWithTextareaInput — removable 은 이 둘만 붙인다).
// 첫 줄이 코멘트, 그 아래가 내담자 답변 칸
function isCommentBlock(block: ProgramContentType) {
  return (
    !!block.removable &&
    !!block.lines[0]?.some(
      (cell) => cell.content.coach || cell.type === "aiComment"
    )
  );
}

// 내담자에게 보이지 않는 블록 (Agent 가 채우면 안 됨)
function isExcludedBlock(block: ProgramContentType) {
  return block.hideIfPatient || block.isAlwaysHidden;
}

function walk(content: ProgramContentType[], start: number) {
  const path: number[] = [];
  let index: number | undefined = start;
  while (index !== undefined && index < content.length) {
    path.push(index);
    index = getNextBlockIndex(content, index);
  }
  return path;
}

// 분기 선택지(showFromId)에 따라서만 열리는 블록 → 그 블록을 여는 선택지
function getBranchConditions(content: ProgramContentType[]) {
  const conditions = new Map<number, UserSimVisibleIf>();
  content.forEach((block, blockIndex) =>
    block.lines.forEach((line, lineIndex) =>
      line.forEach((cell, cellIndex) => {
        if (cell.type !== "buttongroup" && cell.type !== "select") return;
        const options = (cell.content as SingleSelectionType).options || [];
        if (!options.some((option) => option.showFromId)) return;

        const paths = options.map((option) => {
          const target = content.findIndex(
            (element, index) =>
              index > blockIndex &&
              element.blockId &&
              element.blockId === option.showFromId
          );
          return walk(content, target > -1 ? target : blockIndex + 1);
        });
        new Set(paths.flat()).forEach((index) => {
          const optionIndexes = paths.flatMap((path, optionIndex) =>
            path.includes(index) ? [optionIndex] : []
          );
          if (optionIndexes.length < options.length) {
            conditions.set(index, {
              key: getCellKey(blockIndex, lineIndex, cellIndex),
              options: optionIndexes,
            });
          }
        });
      })
    )
  );
  return conditions;
}

// 퀴즈 정답 (GuideTypographyLine 의 answerList)
function getQuizAnswer(
  content: ProgramContentType[],
  id: string | undefined,
  options: string[]
) {
  if (!id) return undefined;
  for (const cell of content.flatMap((block) => block.lines.flat())) {
    const answer = cell.content.answerList?.find((each) => each.id === id);
    if (answer) {
      return answer.answerIndex ?? options.indexOf(answer.value || "");
    }
  }
  return undefined;
}

// 마치기 알림용 — 보낸 소감은 칸이 비워지므로, 이 활동지에서 보낸 소감을 소감 칸 value 로 싣는다 (여러 번이면 빈 줄로 이어서)
export function withSentFeedback(
  req: UserSimStreamRequest,
  content: ProgramContentType[]
): UserSimStreamRequest {
  const sent = new Map<string, string>();
  content.forEach((block, blockIndex) =>
    block.lines.forEach((line, lineIndex) =>
      line.forEach((cell, cellIndex) => {
        const feedbacks = (cell.content as FeedbackType).sentFeedbacks;
        if (cell.type === "feedback" && feedbacks?.length) {
          sent.set(
            getCellKey(blockIndex, lineIndex, cellIndex),
            feedbacks.join("\n\n")
          );
        }
      })
    )
  );
  if (sent.size === 0) return req;
  return {
    ...req,
    fields: req.fields.map((field) =>
      field.input === "text" && sent.has(field.key)
        ? { ...field, fill: "none", value: sent.get(field.key) as string }
        : field
    ),
  };
}

export async function buildUserSimRequest(
  params: UserSimRequestParams
): Promise<UserSimStreamRequest> {
  const { taskKey, content, t, t_common } = params;

  // Cell.tsx 의 translatedText 와 같은 규칙
  const translate = (translationKey?: string, text?: string) => {
    if (!translationKey) return text || "";
    if (translationKey.startsWith("common")) {
      return t_common(translationKey.split(":")[1] || "");
    }
    if (translationKey === "$A$") return t_common(translationKey);
    const [otherTaskId, otherVersion, otherKey] = translationKey.split(":");
    if (isTaskIdValid(otherTaskId)) {
      return i18n.getFixedT(
        null,
        "translation",
        `task.activity.${otherTaskId}.${
          otherVersion?.startsWith("v") ? otherVersion : "v1"
        }`
      )(otherKey || otherVersion || "");
    }
    return t(translationKey);
  };

  const getOptions = (cell: CellType) =>
    ((cell.content as SingleSelectionType).options || []).map(
      (option) =>
        option.text ||
        (option.translationKey?.startsWith("common")
          ? t_common(option.translationKey.split(":")[1] || "")
          : t(option.translationKey || ""))
    );

  const conditions = getBranchConditions(content);
  // 문구는 끝까지 연 화면 기준 — 같은 id 가 여러 블록에 있으면 getReferenceCell 이 열린 블록만 보기 때문
  // (lines 는 그대로라 칸 객체는 content 와 같다)
  const shownContent = content.map((block) => ({
    ...block,
    isShown: true,
    isHidden: false,
  }));
  const keyOfCell = new Map<CellType, string>();
  const cellOfKey = new Map<string, CellType>();
  const fieldOfKey = new Map<string, UserSimField>();

  // 코멘트 턴 — 마친 활동지·기록에 코치·AI 코멘트가 달려 다시 열린 경우. 답할 코멘트가 있으면 답변 칸만 채운다
  // (처음 마칠 때 비워 둔 생각함정 칸, 보내서 비워진 소감 칸 등은 그대로 둔다)
  const isReplyOnly = content.some(
    (block) =>
      !isExcludedBlock(block) &&
      isCommentBlock(block) &&
      block.lines
        .flat()
        .some(
          (cell) =>
            cell.type === "textarea" &&
            !cell.content.coach &&
            !cell.content.readonly &&
            isCellEmpty(cell)
        )
  );

  // 1) 칸 정의
  content.forEach((block, blockIndex) => {
    if (isExcludedBlock(block)) return;
    block.lines.forEach((line, lineIndex) =>
      line.forEach((cell, cellIndex) => {
        const key = getCellKey(blockIndex, lineIndex, cellIndex);
        keyOfCell.set(cell, key);
        cellOfKey.set(key, cell);
        const field = toField(cell, key, block, blockIndex);
        if (field) fieldOfKey.set(key, field);
      })
    );
  });

  // 여러 개 선택 칸에서 선택지를 고르면 블록 끝에 생기는 칸 (예: 감정 → 감정별 세부 단어).
  // 아직 고르지 않았으면 화면에 없으므로 선택지별 칸을 visible_if 와 함께 미리 담는다.
  const getOptionItems = (
    field: UserSimField,
    cell: CellType,
    block: ProgramContentType,
    blockIndex: number
  ): OptionItem[] => {
    if (field.input !== "multi_choice" || field.value !== null) return [];
    const options = (cell.content as MultipleSelectionType).options || [];
    return options.flatMap((option, optionIndex) =>
      (option.additionalContent || []).flatMap((line, lineIndex) =>
        line.flatMap((optionCell, cellIndex): OptionItem[] => {
          const content = optionCell.content as TypographyType;
          if (optionCell.type === "typography" && content.isLabel) {
            return [{ label: content.translationKey || "" }];
          }
          const optionField = toField(
            optionCell,
            getOptionCellKey(field.key, optionIndex, lineIndex, cellIndex),
            block,
            blockIndex
          );
          return optionField
            ? [
                {
                  field: {
                    ...optionField,
                    visible_if: { key: field.key, options: [optionIndex] },
                  },
                },
              ]
            : [];
        })
      )
    );
  };

  function toField(
    cell: CellType,
    key: string,
    block: ProgramContentType,
    blockIndex: number
  ): UserSimField | undefined {
    const c = cell.content;
    const isInput = INPUT_TYPES.includes(cell.type);
    // TODO: 6-12-A 중간 믿음 고르기(selectIntermediateBelief)를 choice 칸으로 — 선택지는 셀이 띄우는 사례 노트의 중간 믿음 목록.
    //  지금은 칸이 아니라 자동 채우기 후에도 마치기가 막힌다 (isSelectIntermediateBeliefFilled).
    //  ConceptItem(6-3·6-6·6-9·6-12)도 screen 에 value 로 (비면 "(코치 미입력)")
    if (
      !isInput &&
      cell.type !== "date" &&
      cell.type !== "time" &&
      cell.type !== "selfCheck"
    ) {
      return;
    }
    if (c.coach) return;

    const empty = isCellEmpty(cell);
    // 코멘트 답변 칸 — 답변 칸 id 가 코멘트를 단 칸 id 에서 오므로(생각함정 칸이면 optional 이 됨) 따로 본다
    const isCommentReply = cell.type === "textarea" && isCommentBlock(block);
    const fill: UserSimFill =
      c.readonly || !empty || (isReplyOnly && !isCommentReply)
        ? "none"
        : !isCommentReply &&
            (c.optional || c.id?.startsWith(THOUGHT_TRAP_ID_PREFIX))
          ? "optional"
          : "required";
    const base = {
      key,
      block: blockIndex,
      name: c.id ?? null,
      label: null,
      question: null,
      visible_if: conditions.get(blockIndex) ?? null,
    };

    if (cell.type === "textarea") {
      return {
        ...base,
        ...(isCommentReply && { name: "comment_reply" }),
        input: "text",
        fill,
        value: empty ? null : (c.value as string),
        placeholder: (c as TextareaType).placeholder ?? null,
      };
    }
    // 마지막 소감 칸 — 코치에게 채팅으로 보내는 메시지. 채워 두기만 하고 보내기는 사용자가 누른다
    if (cell.type === "feedback") {
      return {
        ...base,
        name: "feedback",
        input: "text",
        fill,
        value: empty ? null : (c.value as string),
        placeholder: t_common("소감 placeholder"),
      };
    }
    if (cell.type === "percentage") {
      const p = c as PercentageType;
      const autoIdList = p.complementPercentageIdList;
      // 자동 계산 칸: 화면에 남은 값은 낡았을 수 있어 나머지 칸이 다 차 있을 때만 다시 계산 (Cell.tsx 와 같은 식)
      const autoValue =
        autoIdList &&
        autoIdList.every((id) => {
          const ref = getReferenceCell(content, id);
          return ref && !isCellEmpty(ref);
        })
          ? Math.max(0, 100 - getPercentageSum(content, autoIdList))
          : null;
      return {
        ...base,
        input: "percentage",
        fill: autoIdList ? "none" : fill,
        value: autoIdList ? autoValue : empty ? null : Number(p.value),
        min: p.min ?? 0,
        max: p.max ?? 100,
        allow_zero: !p.warnIfZero,
        placeholder:
          p.placeholder ??
          (c.id?.startsWith("영향받는 정도") || c.id === "percentageThought"
            ? t_common("영향 정도 placeholder")
            : null),
      };
    }
    if (cell.type === "buttongroup" || cell.type === "select") {
      const options = getOptions(cell);
      const selected = (c as SingleSelectionType).selectedIndex;
      // 생각기록지 명상 권유는 항상 "아니요" (마칠 때 명상훈련지가 자동으로 생기지 않도록)
      const meditationNo =
        block.blockId === "meditation_0"
          ? options.findIndex((each) => each === "아니요")
          : -1;
      const answer =
        meditationNo > -1
          ? meditationNo
          : getQuizAnswer(content, c.id, options);
      // 답할 코멘트가 있으면 빈 퀴즈·명상 권유도 채우지 않으므로 정답을 싣지 않는다
      const value = !empty
        ? (selected ?? options.indexOf(c.value || ""))
        : isReplyOnly
          ? null
          : (answer ?? null);
      return {
        ...base,
        input: "choice",
        fill: value !== null ? "none" : fill,
        value,
        options,
      };
    }
    // 자가 검사 문항 — 선택지는 응답 문구(셀과 같은 번역), 순서는 evaluation 의 key 순서.
    // 셀은 점수만 저장하므로 같은 점수인 응답이 여럿이면 첫 번째 응답으로 (applyAutoFill 이 번호 → 점수로 되돌린다)
    if (cell.type === "selfCheck") {
      const { evaluation, selectedScore, selfCheckId } = c as SelfCheckType;
      const keys = Object.keys(evaluation);
      return {
        ...base,
        name: selfCheckId,
        input: "choice",
        fill,
        value: empty
          ? null
          : keys.findIndex((each) => evaluation[each] === selectedScore),
        options: keys.map((each) => t_common(each)),
      };
    }
    if (cell.type === "buttongroup_multipleChoice") {
      const selected = (c as MultipleSelectionType).selectedIndices;
      return {
        ...base,
        input: "multi_choice",
        fill,
        value: !empty && selected?.length ? selected : null,
        options: getOptions(cell),
        select_count:
          (c as MultipleSelectionType).requiredSelectionCount ?? null,
      };
    }
    return {
      ...base,
      input: cell.type as "date" | "time",
      fill: "none",
      value: (c.value as string) ?? null,
    };
  }

  // Agent 가 채울 칸을 가리키는 "$A$" 는 {{key}} 로, 비어 있는 기록·코치 입력은 표시 문구로 남긴다
  const resolveFromData = async (cell: CellType, template: string) => {
    const dataMapping =
      (cell.content as TypographyFromDataType).dataMapping || {};
    const placeholders: Record<string, string> = {};
    const emptyTexts: Record<string, string> = {};
    Object.entries(dataMapping).forEach(([name, mapping]) => {
      if (mapping.taskId || mapping.value || mapping.id === undefined) return;
      const ref = getReferenceCell(shownContent, mapping.id as string);
      const key = ref && keyOfCell.get(ref);
      const field = key && fieldOfKey.get(key);
      if (field && field.fill !== "none") {
        placeholders[name] = `{{${key}}}`;
      } else if (ref) {
        emptyTexts[name] =
          ref.type === "typographyFromDashboard" ||
          ref.type === "typographyFromConceptNote"
            ? "(기록 없음)"
            : ref.type === "preset" || ref.content.coach
              ? "(코치 미입력)"
              : "(비어 있음)";
      }
    });
    const mapping: Record<string, string> = {
      ...(await getTextMappingFromDataMapping({
        taskKey,
        data: shownContent,
        dataMapping,
        isCoach: false,
        accessToken: params.accessToken,
        pid: params.pid,
        t,
        taskDataState: params.taskDataState,
        setTaskDataState: params.setTaskDataState,
      })),
      ...placeholders,
    };
    return template
      .split("$")
      .map((each, index) =>
        index % 2 === 0
          ? each
          : each.startsWith("eval")
            ? "(자동 계산)"
            : mapping[each] || emptyTexts[each] || ""
      )
      .join("");
  };

  // 2) 화면 문구 + 칸 순서
  const screen: UserSimBlock[] = [];
  const fields: UserSimField[] = [];
  // 칸의 question = 가장 최근 블록 안내 문구
  const current = { question: null as string | null };

  for (let blockIndex = 0; blockIndex < content.length; blockIndex++) {
    const block = content[blockIndex];
    if (isExcludedBlock(block)) continue;
    const items: UserSimItem[] = [];
    // 이름표: 진한 이름표(solid)가 묶음을 시작하고, 흐린 이름표(dimmed)는 바로 아래 값·문장의 머리글이다
    let labels: { text: string; dimmed: boolean }[] = [];
    const consumeDimmedLabel = () => {
      const index = labels.findIndex((label) => label.dimmed);
      if (index > -1) labels.splice(index, 1);
    };
    // 블록 첫 줄 값(코치·AI 코멘트 등)도 그 블록의 안내 문구다
    const pushValue = (text: string, lineIndex: number) => {
      items.push({ type: "value", text });
      if (lineIndex === 0) current.question = text;
      else consumeDimmedLabel();
    };
    const pushField = (field: UserSimField) => {
      fields.push({
        ...field,
        label:
          labels.length > 0
            ? labels.map((label) => label.text).join(" > ")
            : null,
        question: current.question,
      });
      items.push({ type: "field", key: field.key });
      labels = [];
    };
    const optionItems: OptionItem[] = [];
    // 블록 첫 줄 문장 = 안내 문구(question), 그 아래 문장은 예시 등 내용
    const pushText = (text: string, lineIndex: number) => {
      items.push({ type: "text", text });
      if (lineIndex === 0) current.question = text;
      else consumeDimmedLabel();
    };

    for (let lineIndex = 0; lineIndex < block.lines.length; lineIndex++) {
      for (const cell of block.lines[lineIndex]) {
        const c = cell.content;
        const key = keyOfCell.get(cell) as string;
        const field = fieldOfKey.get(key);

        if (field) {
          pushField(field);
          optionItems.push(...getOptionItems(field, cell, block, blockIndex));
        } else if (cell.type === "typography") {
          const typography = c as TypographyType & {
            isAlert?: boolean;
            isDimmed?: boolean;
          };
          if (typography.isAlert) continue;
          if (typography.isLabel) {
            const text = `${typography.prefixLabelIndex || ""}${
              typography.translationKey || ""
            } ${typography.labelIndex || ""}`.trim();
            if (!typography.isDimmed) labels = [];
            labels.push({ text, dimmed: !!typography.isDimmed });
            items.push({ type: "label", text });
            continue;
          }
          const text = translate(typography.translationKey, typography.text);
          if (!text.trim()) continue;
          if (typography.isHeader) {
            items.push({ type: "header", text });
          } else {
            pushText(text, lineIndex);
          }
        } else if (cell.type === "typographyFromData") {
          const typography = c as TypographyFromDataType &
            TypographyType & { isAlert?: boolean };
          if (typography.isAlert) continue;
          const template = translate(
            typography.translationKey,
            typography.text
          );
          const text = await resolveFromData(cell, template);
          if (template.trim() === "$A$") {
            pushValue(text || "(비어 있음)", lineIndex);
          } else if (typography.isHeader) {
            items.push({ type: "header", text });
          } else {
            pushText(text, lineIndex);
          }
        } else if (
          cell.type === "typographyFromDashboard" ||
          cell.type === "typographyFromConceptNote"
        ) {
          pushValue(c.value?.trim() || "(기록 없음)", lineIndex);
        } else if (
          cell.type === "preset" ||
          cell.type === "aiComment" ||
          (c.coach && INPUT_TYPES.includes(cell.type))
        ) {
          pushValue(c.value?.trim() || "(코치 미입력)", lineIndex);
        }
      }
    }

    // 선택지를 고르면 생기는 칸은 블록 끝 (ButtonGroupMultipleChoiceCell 이 lines 끝에 붙임)
    for (const item of optionItems) {
      if ("label" in item) {
        labels = [{ text: item.label, dimmed: false }];
        items.push({ type: "label", text: item.label });
      } else {
        pushField(item.field);
      }
    }

    if (items.length > 0) {
      screen.push({
        block: blockIndex,
        visible_if: conditions.get(blockIndex) ?? null,
        items,
      });
    }
  }

  // 3) 칸 사이 제약
  const thoughtTrapKeys = new Map<number, string[]>();
  fields.forEach((field) => {
    if (field.name?.startsWith(THOUGHT_TRAP_ID_PREFIX)) {
      thoughtTrapKeys.set(field.block, [
        ...(thoughtTrapKeys.get(field.block) || []),
        field.key,
      ]);
    }
  });
  const atLeastOneRules: UserSimRule[] = Array.from(
    thoughtTrapKeys.values()
  ).map((keys) => ({ type: "at_least_one", keys }));

  // 자동 계산 칸 (100 − 합)
  const autoSumRules: UserSimRule[] = fields.flatMap((field) => {
    const idList = cellOfKey.get(field.key)?.content.complementPercentageIdList;
    if (!idList) return [];
    return [
      {
        type: "auto_sum",
        keys: idList.flatMap((id) => {
          const ref = getReferenceCell(content, id);
          const key = ref && keyOfCell.get(ref);
          return key ? [key] : [];
        }),
        auto_key: field.key,
        total: 100,
      },
    ];
  });
  const rules = [...autoSumRules, ...atLeastOneRules];

  // 생각기록지 힘든 일/좋은 일 — 좋은 일은 선택지 흔적(selectedIndex 1)으로 남는다 (useGetBlankThoughtRecordData)
  const sentimentCell = content
    .flatMap((block) => block.lines.flat())
    .find((cell) => cell.content.id === "sentiment");
  const sentiment = !sentimentCell
    ? null
    : sentimentCell.content.value === "positive" ||
        (sentimentCell.content as SingleSelectionType).selectedIndex === 1
      ? "positive"
      : "negative";

  const taskIdWithNumber = getTaskIdFromTaskKey(taskKey);
  const [taskId, automaticThoughtId] = taskIdWithNumber
    .split("#")[0]
    .split("_");
  const patientId = getPatientIdFromTaskKey(taskKey);

  return {
    version: 1,
    task: {
      key: taskKey,
      id: taskId,
      automatic_thought_id: automaticThoughtId ?? null,
      patient_id: Number.isNaN(patientId) ? null : patientId,
      title: params.title,
      record_key: params.thoughtRecordKey ?? null,
      sentiment,
    },
    screen,
    fields,
    rules,
  };
}
